import React, { useEffect, useMemo, useState } from 'react';
import { MessageCircle, X, Send, CheckCheck } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import { resolveImageUrl } from '../utils/urls';

const DEFAULT_GREETING =
  'Halo! Selamat datang di JILONG Indonesia. Ada yang bisa kami bantu terkait Fusion Splicer, OTDR, atau produk lainnya?';
const DEFAULT_STATUS = 'Biasanya membalas dalam 1 jam';
const DEFAULT_AGENT_NAME = 'Sales JILONG Indonesia';
const DEFAULT_QUICK_REPLIES = [
  'Saya ingin tanya harga Fusion Splicer',
  'Tersedia demo unit / trial?',
  'Butuh informasi service center terdekat',
  'Konsultasi kebutuhan proyek fiber optik',
];

const formatTime = () => {
  const d = new Date();
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
};

// Business hours (WIB):
//   Mon–Fri: 08:00–17:00
//   Sat:     08:00–12:00
//   Sun:     Closed
const isBusinessHours = () => {
  const d = new Date();
  const day = d.getDay(); // 0 Sun, 1 Mon … 6 Sat
  const hour = d.getHours();
  if (day === 0) return false;                 // Sunday
  if (day === 6) return hour >= 8 && hour < 12; // Saturday half-day
  return hour >= 8 && hour < 17;                // Mon–Fri
};

const WhatsAppBubble = () => {
  const { config } = useSiteContent();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserDismissed, setTeaserDismissed] = useState(false);

  const enabled = config.whatsapp_widget_enabled !== false; // opt-out by default
  const rawWa = config.whatsapp || '';
  const waNumber = rawWa.replace(/[^0-9]/g, '');

  const greeting = (config.whatsapp_greeting || DEFAULT_GREETING).trim();
  const agentName = (config.whatsapp_agent_name || DEFAULT_AGENT_NAME).trim();
  const statusText = (config.whatsapp_status || DEFAULT_STATUS).trim();
  const online = isBusinessHours();

  const quickReplies = useMemo(() => {
    const custom = Array.isArray(config.whatsapp_quick_replies) ? config.whatsapp_quick_replies : [];
    const cleaned = custom.map((s) => String(s || '').trim()).filter(Boolean);
    return cleaned.length ? cleaned : DEFAULT_QUICK_REPLIES;
  }, [config.whatsapp_quick_replies]);

  // Show floating teaser bubble after 6s on first visit if never dismissed.
  useEffect(() => {
    if (!enabled) return;
    try {
      const dismissed = sessionStorage.getItem('wa_teaser_dismissed') === '1';
      if (dismissed) { setTeaserDismissed(true); return; }
    } catch { /* noop */ }
    const t = setTimeout(() => setShowTeaser(true), 6000);
    return () => clearTimeout(t);
  }, [enabled]);

  if (!enabled || !waNumber) return null;

  const brandLogo = resolveImageUrl(config.logo) || '';
  const hasLogo = !!brandLogo;

  const dismissTeaser = () => {
    setShowTeaser(false);
    setTeaserDismissed(true);
    try { sessionStorage.setItem('wa_teaser_dismissed', '1'); } catch { /* noop */ }
  };

  const openChat = () => {
    setOpen(true);
    dismissTeaser();
  };

  const sendToWhatsApp = (text) => {
    const body = (text || message || '').trim() || 'Halo, saya ingin bertanya tentang produk JILONG.';
    const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noreferrer');
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendToWhatsApp();
  };

  return (
    <>
      {/* Chat card */}
      {open && (
        <div
          data-testid="wa-chat-card"
          className="fixed z-[1000] bottom-24 right-4 sm:right-6 w-[92vw] max-w-sm rounded-2xl overflow-hidden shadow-2xl border border-black/5 bg-[#e5ddd5] animate-[waPop_.25s_ease-out]"
          style={{ maxHeight: 'calc(100vh - 8rem)' }}
        >
          {/* Header */}
          <div className="bg-[#075e54] text-white px-4 py-3 flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center overflow-hidden">
                {brandLogo ? (
                  <img src={brandLogo} alt="JILONG" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-extrabold text-lg tracking-tight">JL</span>
                )}
              </div>
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#075e54] ${online ? 'bg-[#25d366]' : 'bg-gray-400'}`}
                aria-label={online ? 'online' : 'offline'}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate" data-testid="wa-agent-name">{agentName}</p>
              <p className="text-[11px] text-white/80 truncate">
                {online ? 'Online sekarang · ' : 'Offline · '}
                {statusText}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
              aria-label="Tutup"
              data-testid="wa-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body – message thread */}
          <div
            className="px-4 py-5 space-y-3 overflow-y-auto"
            style={{
              maxHeight: 'calc(100vh - 22rem)',
              backgroundImage:
                'radial-gradient(circle at 20% 10%, rgba(0,0,0,0.03) 0 2px, transparent 3px), radial-gradient(circle at 80% 70%, rgba(0,0,0,0.03) 0 2px, transparent 3px)',
              backgroundSize: '30px 30px',
            }}
          >
            <div className="flex justify-start">
              <div className="max-w-[85%] bg-white rounded-lg rounded-tl-none px-3 py-2 shadow-sm">
                <p className="text-sm text-gray-800 whitespace-pre-line leading-snug">{greeting}</p>
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-[10px] text-gray-500">{formatTime()}</span>
                </div>
              </div>
            </div>

            {/* Quick replies */}
            <div className="pt-1">
              <p className="text-[11px] text-gray-500 mb-2 px-1">Balasan cepat</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((q, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => sendToWhatsApp(q)}
                    className="text-xs bg-white text-[#075e54] border border-[#075e54]/20 hover:bg-[#075e54] hover:text-white transition-colors px-3 py-1.5 rounded-full shadow-sm"
                    data-testid={`wa-quick-reply-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Composer */}
          <form onSubmit={onSubmit} className="bg-[#f0f0f0] px-3 py-2 flex items-center gap-2 border-t border-black/5">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ketik pesan…"
              className="flex-1 bg-white rounded-full px-4 py-2 text-sm border border-transparent focus:outline-none focus:border-[#25d366] focus:ring-2 focus:ring-[#25d366]/20"
              data-testid="wa-input"
            />
            <button
              type="submit"
              className="w-10 h-10 rounded-full bg-[#25d366] hover:bg-[#20bd5c] flex items-center justify-center text-white shadow-md transition-colors"
              aria-label="Kirim"
              data-testid="wa-send"
            >
              <Send size={16} />
            </button>
          </form>
          <div className="bg-white/60 px-4 py-1.5 text-[10px] text-gray-500 flex items-center justify-center gap-1 border-t border-black/5">
            <CheckCheck size={12} className="text-[#25d366]" />
            <span>Dilanjutkan ke aplikasi WhatsApp</span>
          </div>
        </div>
      )}

      {/* Teaser message bubble */}
      {!open && showTeaser && !teaserDismissed && (
        <button
          type="button"
          onClick={openChat}
          data-testid="wa-teaser"
          className="fixed z-[999] bottom-24 right-4 sm:right-6 max-w-xs bg-white rounded-2xl rounded-br-none shadow-2xl px-4 py-3 pr-8 text-left border border-black/5 animate-[waPop_.3s_ease-out] group"
        >
          <span className="block text-[11px] font-semibold text-[#075e54]">{agentName}</span>
          <span className="block text-sm text-gray-700 line-clamp-2 mt-0.5">
            {greeting}
          </span>
          <span className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-black/5" onClick={(e) => { e.stopPropagation(); dismissTeaser(); }}>
            <X size={12} className="text-gray-400" />
          </span>
        </button>
      )}

      {/* Floating action button */}
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openChat())}
        aria-label={open ? 'Tutup WhatsApp' : 'Chat via WhatsApp'}
        data-testid="wa-fab"
        className="fixed z-[1001] bottom-5 right-4 sm:right-6 w-14 h-14 rounded-full bg-[#25d366] text-white shadow-xl hover:bg-[#20bd5c] flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
      >
        {/* Pulsing ring */}
        {!open && !teaserDismissed && (
          <span className="absolute inset-0 rounded-full bg-[#25d366] animate-ping opacity-60" />
        )}
        {open ? <X size={26} /> : <MessageCircle size={26} className="relative z-10" />}
        {/* Unread badge – only when teaser has not been seen yet */}
        {!open && !teaserDismissed && (
          <span
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow"
            data-testid="wa-badge"
          >
            1
          </span>
        )}
      </button>

      {/* Local keyframes */}
      <style>{`
        @keyframes waPop {
          0% { opacity: 0; transform: translateY(8px) scale(.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

// Keep Check import from being flagged as unused; harmless in prod bundles.
export default WhatsAppBubble;
